import { Link } from 'react-router-dom'
import { TriangleAlert, OctagonAlert, UserX, ArrowRight } from 'lucide-react'
import type { WireArticle } from '../../api/types'

// ── isActionNeeded (shared utility) ──────────────────────────────────────────

const STAGE_ASSIGNMENT: Record<string, keyof NonNullable<WireArticle['workflow']>> = {
  qc_review:            'qcAssignedTo',
  layout_review:        'layoutAssignedTo',
  editorial_approval:   'editorialAssignedTo',
  post_pub_review:      'postPubAssignedTo',
}

export function isActionNeeded(article: WireArticle, userSub: string): boolean {
  const stage = article.workflow?.stage
  if (!stage) return false
  const field = STAGE_ASSIGNMENT[stage]
  if (!field) return false
  return article.workflow[field] === userSub
}

// ── NeedsAttentionCard ────────────────────────────────────────────────────────

const STAGE_COLOR: Record<string, string> = {
  qc_review:          'bg-amber-3 text-amber-11 border-amber-6',
  layout_review:      'bg-blue-3 text-blue-11 border-blue-6',
  editorial_approval: 'bg-violet-3 text-violet-11 border-violet-6',
  post_pub_review:    'bg-orange-3 text-orange-11 border-orange-6',
}

const STAGE_ORDER: Record<string, number> = {
  qc_review: 0, layout_review: 1, editorial_approval: 2,
  scheduled: 3, post_pub_review: 4,
}

const BLOCK_REASON_LABEL: Record<string, string> = {
  waiting_for_author: 'Waiting on Author',
  other:              'Other',
}

function stageLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function timeBlocked(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h`
  return `${Math.floor(hrs / 24)}d`
}

function byStage(a: WireArticle, b: WireArticle) {
  return (STAGE_ORDER[a.workflow?.stage ?? ''] ?? 99) - (STAGE_ORDER[b.workflow?.stage ?? ''] ?? 99)
}

interface NeedsAttentionCardProps {
  articles: WireArticle[]
  loading: boolean
  embedded?: boolean
}

export function NeedsAttentionCard({ articles, loading, embedded = false }: NeedsAttentionCardProps) {
  // Blocked: has an active block
  const blocked = articles.filter(
    a => Boolean(a.workflow?.activeBlock)
  )

  // Needs Assignment: in an assignable stage with no one assigned, and not already blocked
  const unassigned = articles
    .filter(a => {
      if (a.workflow?.activeBlock) return false
      const field = STAGE_ASSIGNMENT[a.workflow?.stage ?? '']
      if (!field) return false
      return !a.workflow[field]
    })
    .sort(byStage)

  const totalCount = blocked.length + unassigned.length

  const body = (
    <div className="divide-y divide-line-subtle overflow-y-auto" style={{ maxHeight: 360 }}>
      {loading && (
        <div className="space-y-3 p-5">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 animate-pulse rounded bg-fill" />
          ))}
        </div>
      )}

      {!loading && totalCount === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="font-sans text-sm font-semibold text-fg-secondary">All clear</p>
          <p className="mt-1 font-sans text-xs text-fg-secondary">
            No blocked or unassigned articles.
          </p>
        </div>
      )}

      {!loading && blocked.length > 0 && (
        <div className="flex items-center gap-1.5 bg-red-2 px-5 py-1.5">
          <OctagonAlert size={10} className="text-red-11" />
          <span className="font-sans text-[9px] font-bold uppercase tracking-widest text-red-11">
            Blocked
          </span>
          <span className="ml-auto rounded-full bg-red-4 px-1.5 py-px font-sans text-[9px] font-bold text-red-11">
            {blocked.length}
          </span>
        </div>
      )}
      {!loading && blocked.map(article => {
        const block = article.workflow?.activeBlock
        return (
          <Link
            key={article.id}
            to={`/admin/articles/${article.id}`}
            className="group flex items-center justify-between gap-3 px-5 py-3 hover:bg-red-2"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-sans text-sm font-semibold text-fg group-hover:text-red-11">
                {article.title || <span className="italic text-fg-secondary">Untitled</span>}
              </p>
              <p className="mt-0.5 font-sans text-[10px] text-fg-secondary">
                {block
                  ? `${BLOCK_REASON_LABEL[block.reason] ?? block.reason}${block.blockedByName ? ` · ${block.blockedByName}` : ''}`
                  : (article.documentId ?? article.id)
                }
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {block?.blockedAt && (
                <span className="rounded bg-red-3 px-1.5 py-px font-sans text-[10px] font-semibold tabular-nums text-red-11">
                  {timeBlocked(block.blockedAt)}
                </span>
              )}
              <ArrowRight size={12} className="text-red-11 opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </Link>
        )
      })}

      {!loading && unassigned.length > 0 && (
        <div className="flex items-center gap-1.5 bg-amber-2 px-5 py-1.5">
          <UserX size={10} className="text-amber-11" />
          <span className="font-sans text-[9px] font-bold uppercase tracking-widest text-amber-11">
            Needs Assignment
          </span>
          <span className="ml-auto rounded-full bg-amber-4 px-1.5 py-px font-sans text-[9px] font-bold text-amber-11">
            {unassigned.length}
          </span>
        </div>
      )}
      {!loading && unassigned.map(article => {
        const stage = article.workflow?.stage ?? ''
        const colorClass = STAGE_COLOR[stage] ?? 'bg-fill text-fg-secondary border-line'
        return (
          <Link
            key={article.id}
            to={`/admin/articles/${article.id}`}
            className="group flex items-center justify-between gap-3 px-5 py-3 hover:bg-amber-2"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-sans text-sm font-semibold text-fg group-hover:text-amber-11">
                {article.title || <span className="italic text-fg-secondary">Untitled</span>}
              </p>
              <p className="mt-0.5 font-sans text-[10px] text-fg-secondary">
                {article.documentId ?? article.id}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className={`rounded border px-2 py-0.5 font-sans text-[9px] font-bold uppercase tracking-wide ${colorClass}`}>
                {stageLabel(stage)}
              </span>
              <ArrowRight size={12} className="text-amber-11 opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </Link>
        )
      })}
    </div>
  )

  if (embedded) {
    return <div>{body}</div>
  }

  return (
    <section className="flex flex-col rounded border border-amber-6 bg-surface">

      {/* Header */}
      <div className="flex items-center justify-between border-b border-amber-6 bg-amber-2 px-5 py-3">
        <div className="flex items-center gap-2">
          <TriangleAlert size={14} className="text-amber-11" />
          <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-amber-11">
            Needs Attention
          </span>
        </div>
        {!loading && (
          <span className={`rounded-full px-2 py-0.5 font-sans text-[10px] font-bold ${
            totalCount > 0 ? 'bg-amber-4 text-amber-11' : 'bg-fill text-fg-secondary'
          }`}>
            {totalCount}
          </span>
        )}
      </div>

      {/* Body */}
      {body}
    </section>
  )
}
